import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Users, Bath, BedDouble, Flame, UtensilsCrossed, Wifi, Car, ArrowRight } from 'lucide-react';
import { Gallery, GalleryPhoto } from './Gallery';
import { useLanguage } from '../context/LanguageContext';
interface Accommodation {
  id: string;
  name: string;
  badge: string;
  capacity: string;
  description: string;
  features: {
    icon: React.ElementType;
    label: string;
  }[];
  photos: GalleryPhoto[];
}
export function Cabins() {
  const {
    t
  } = useLanguage();
  const [active, setActive] = useState('dos-hab');
  const heating = {
    icon: Flame,
    label: t('Calefacción tiro balanceado', 'Balanced-flue heating')
  };
  const wifi = {
    icon: Wifi,
    label: t('Wifi de alta velocidad', 'High-speed Wi-Fi')
  };
  const quincho = {
    icon: UtensilsCrossed,
    label: t('Quincho con parrilla', 'Quincho with grill')
  };
  const parking = {
    icon: Car,
    label: t('Estacionamiento en el predio', 'On-site parking')
  };
  const accommodations: Accommodation[] = [{
    id: 'dos-hab',
    name: t('Cabaña c/ 2 habitaciones', 'Cabin with 2 bedrooms'),
    badge: t('Ideal para familias y grupos', 'Ideal for families and groups'),
    capacity: t('Hasta 6 huéspedes', 'Up to 6 guests'),
    description: t('La opción más amplia del predio: dos habitaciones, living comedor con ventanales al jardín y cocina totalmente equipada. Con quincho y parrilla propios para compartir un asado.', 'The largest option on the property: two bedrooms, a living-dining room with garden-facing windows and a fully equipped kitchen. With its own quincho and grill to share an asado.'),
    features: [{
      icon: BedDouble,
      label: t('2 habitaciones', '2 bedrooms')
    }, heating, quincho, parking],
    photos: [{
      src: 'https://cdn.magicpatterns.com/uploads/ab1ym6K8e3CDWs5XaceMa7/20230607_121436.jpg',
      alt: t('Living de la cabaña con sillón y ventanales de troncos con vista al jardín', 'Cabin living room with sofa and log-framed windows looking onto the garden')
    }, {
      src: 'https://cdn.magicpatterns.com/uploads/3FTh2umoCABixyyb8XLNaf/20230607_121736.jpg',
      alt: t('Living comedor de la cabaña con sillón, mesa de madera y ventanales al jardín', 'Cabin living-dining room with sofa, wooden table and windows onto the garden')
    }, {
      src: 'https://cdn.magicpatterns.com/uploads/iU99Qh8N76kJUVBFA1XE12/20230607_124033.jpg',
      alt: t('Cocina equipada con heladera, mesada, pileta y cocina a gas', 'Equipped kitchen with fridge, counter, sink and gas stove')
    }, {
      src: 'https://cdn.magicpatterns.com/uploads/uKxLYyWBwvEw4pp2gfvo61/20230601_154059.jpg',
      alt: t('Baño de la cabaña con ducha, lavatorio, bidet e inodoro', 'Cabin bathroom with shower, washbasin, bidet and toilet')
    }, {
      src: 'https://cdn.magicpatterns.com/uploads/buRDYXYgpbAS6h5vTYFMfp/20230607_123137.jpg',
      alt: t('Habitación de la cabaña con paredes de madera y velador encendido', 'Cabin bedroom with wooden walls and a lit bedside lamp')
    }, {
      src: 'https://cdn.magicpatterns.com/uploads/8SnTNrcmiwk8kdieSTCM2h/20230607_122320.jpg',
      alt: t('Habitación con cama matrimonial, cama individual y TV', 'Bedroom with a double bed, a single bed and a TV')
    }, {
      src: 'https://cdn.magicpatterns.com/uploads/naRM5DNv8yHw4cXRTtjbKg/20230928_165417.jpg',
      alt: t('Segunda habitación con tres camas individuales', 'Second bedroom with three single beds')
    }]
  }, {
    id: 'una-hab',
    name: t('Cabaña c/ 1 habitación', 'Cabin with 1 bedroom'),
    badge: t('Ideal para parejas y familias', 'Ideal for couples and families'),
    capacity: t('Hasta 5 huéspedes', 'Up to 5 guests'),
    description: t('Cabaña en dos plantas con habitación en planta alta, living comedor con TV y cocina equipada. Cálida durante todo el año y con su propio espacio al aire libre.', 'Two-storey cabin with an upstairs bedroom, living-dining room with TV and an equipped kitchen. Warm all year round and with its own outdoor space.'),
    features: [{
      icon: BedDouble,
      label: t('1 habitación', '1 bedroom')
    }, heating, quincho, wifi],
    photos: [{
      src: 'https://cdn.magicpatterns.com/uploads/gtWYvk9XnLv4BvMrygt9gS/WhatsApp_Image_2026-09-03_at_11.50.36_AM_(3).jpg',
      alt: t('Living comedor con televisor y escalera de troncos a la planta alta', 'Living-dining room with TV and a log staircase to the upper floor')
    }, {
      src: 'https://cdn.magicpatterns.com/uploads/esBu3Q1DTCtReYWm7PPN6e/WhatsApp_Image_2026-09-03_at_11.50.35_AM.jpg',
      alt: t('Comedor de la cabaña con mesa, sillas de madera y puerta de acceso', 'Cabin dining area with table, wooden chairs and the entrance door')
    }, {
      src: 'https://cdn.magicpatterns.com/uploads/n8qATkQdJ6xS5S8yskVkaq/WhatsApp_Image_2026-09-03_at_11.50.36_AM.jpg',
      alt: t('Cocina equipada con heladera, mesada, pileta y cocina a gas', 'Equipped kitchen with fridge, counter, sink and gas stove')
    }, {
      src: 'https://cdn.magicpatterns.com/uploads/sBanST37qqTz7Q1DRgp6Xj/Gemini_Generated_Image_6ig8sh6ig8sh6ig8.jpg',
      alt: t('Baño de la cabaña con ducha, lavatorio, bidet y techo de troncos', 'Cabin bathroom with shower, washbasin, bidet and log ceiling')
    }, {
      src: 'https://cdn.magicpatterns.com/uploads/67NwK6TMP2F4xUkrd6m85q/WhatsApp_Image_2026-09-03_at_11.50.38_AM_(1).jpg',
      alt: t('Habitación amplia con cama matrimonial, camas individuales y cucheta', 'Spacious bedroom with a double bed, single beds and a bunk bed')
    }, {
      src: 'https://cdn.magicpatterns.com/uploads/fVYE9rXKCjwe9gU14iEa25/WhatsApp_Image_2026-09-03_at_11.50.38_AM_(2).jpg',
      alt: t('Habitación en planta alta con cama matrimonial, cucheta y TV', 'Upstairs bedroom with double bed, bunk bed and TV')
    }]
  }, {
    id: 'monoambiente',
    name: t('Monoambiente totalmente equipado', 'Fully equipped studio'),
    badge: t('Ideal para parejas y familias', 'Ideal for couples and families'),
    capacity: t('Hasta 4 huéspedes', 'Up to 4 guests'),
    description: t('Un ambiente integrado con cocina totalmente equipada, zona de descanso y baño propio. Práctico y cálido, perfecto para estadías tranquilas.', 'A single integrated space with a fully equipped kitchen, sleeping area and its own bathroom. Practical and warm, perfect for quiet stays.'),
    features: [{
      icon: UtensilsCrossed,
      label: t('Cocina totalmente equipada', 'Fully equipped kitchen')
    }, heating, wifi, parking],
    photos: [{
      src: 'https://cdn.magicpatterns.com/uploads/p6HSMGbQnd62maDGNKnqmm/WhatsApp_Image_2026-09-04_at_16.12.59.jpg',
      alt: t('Monoambiente con cama matrimonial, cucheta, TV y pared de ladrillo', 'Studio with double bed, bunk bed, TV and a brick wall')
    }, {
      src: 'https://cdn.magicpatterns.com/uploads/k4byVcVMqqtmLLqTcPA1yP/WhatsApp_Image_2026-09-04_at_16.12.592.jpg',
      alt: t('Cocina comedor del monoambiente con mesada, cocina a gas, heladera y mesa', 'Studio kitchen and dining area with counter, gas stove, fridge and table')
    }, {
      src: 'https://cdn.magicpatterns.com/uploads/nEzEwKozfaAmDW1LAnr11e/cabana_7_cama.jpg',
      alt: t('Cama con respaldo de madera contra pared de ladrillo y velador encendido', 'Bed with a wooden headboard against a brick wall with a lit bedside lamp')
    }, {
      src: 'https://cdn.magicpatterns.com/uploads/2Cr4MPPLuV9PDrXPwvH19Q/bano_MONO.jpg',
      alt: t('Baño propio con ducha, lavatorio y espejo con marco de madera', 'Own bathroom with shower, washbasin and a wood-framed mirror')
    }]
  }, {
    id: 'habitaciones',
    name: t('Habitaciones con baño privado', 'Rooms with private bathroom'),
    badge: t('Ideal para estadías cortas', 'Ideal for short stays'),
    capacity: t('Hasta 3 huéspedes', 'Up to 3 guests'),
    description: t('Dos habitaciones cómodas con baño privado, TV y pava eléctrica. La opción práctica para viajes de paso por Bariloche o estadías breves.', 'Two comfortable rooms with a private bathroom, TV and electric kettle. The practical choice for trips passing through Bariloche or short stays.'),
    features: [{
      icon: Bath,
      label: t('Baño privado', 'Private bathroom')
    }, heating, wifi, parking],
    photos: [{
      src: 'https://cdn.magicpatterns.com/uploads/kwAzAmM9coB9DjPzrEDAh2/WhatsApp_Image_2026-04-13_at_14.36.37_(1).jpg',
      alt: t('Habitación con dos camas individuales, techo de troncos, mesa y microondas', 'Room with two single beds, log ceiling, table and microwave')
    }, {
      src: 'https://cdn.magicpatterns.com/uploads/4vq7fV2B9zrjwnKfK7mKba/WhatsApp_Image_2026-04-13_at_14.36.38_(1).jpg',
      alt: t('Habitación con tres camas individuales, televisor y mesa con microondas', 'Room with three single beds, television and a table with a microwave')
    }, {
      src: 'https://cdn.magicpatterns.com/uploads/4pXQxLgczQimdjbrWasQcP/bano.jpg',
      alt: t('Baño privado con ducha, lavatorio y espejo con marco de madera', 'Private bathroom with shower, washbasin and a wood-framed mirror')
    }, {
      src: 'https://cdn.magicpatterns.com/uploads/smeSUCmy38ciQi4EhgspQU/habitacion_1.jpg',
      alt: t('Habitación con cama matrimonial, cama individual, escritorio y microondas', 'Room with a double bed, a single bed, desk and microwave')
    }, {
      src: 'https://cdn.magicpatterns.com/uploads/q9AnLjsTbkxVZwyuswSiba/WhatsApp_Image_2026-04-13_at_14.36.49_(1).jpg',
      alt: t('Habitación con cama matrimonial, cama individual, TV y mesa con pava eléctrica', 'Room with a double bed, a single bed, TV and a table with an electric kettle')
    }]
  }];
  const current = accommodations.find(item => item.id === active) ?? accommodations[0];
  return <section id="cabanas" className="py-24 bg-white" data-id="element-36" data-mp="3alx1l3bpuit3">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-37" data-mp="3aldqa5gkmso1">
        <div className="max-w-2xl mb-10" data-id="element-38" data-mp="35o57c2nlmklq">
          <span className="text-accent font-medium tracking-wider uppercase text-sm mb-3 block" data-id="element-39" data-mp="35kzfwgrhz6mu">
            {t('Alojamiento', 'Accommodation')}
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-primary mb-4" data-id="element-40" data-mp="2wlch82q5kexh">
            {t('Más de 6 cabañas y 2 habitaciones con baño privado', 'More than 6 cabins and 2 rooms with private bathroom')}
          </h2>
          <p className="text-gray-600 text-lg font-light" data-id="element-41" data-mp="1moe9avhqlnkl">
            {t('Elegí el tipo de alojamiento y recorré sus fotos.', 'Choose the type of accommodation and browse its photos.')}
          </p>
        </div>

        {/* Selector de tipo */}
        <div role="tablist" aria-label={t('Tipos de alojamiento', 'Accommodation types')} className="flex flex-wrap gap-3 mb-10" data-id="element-42" data-mp="1hpb51d9kzldo">
          {accommodations.map(item => {
          const isActive = item.id === current.id;
          return <button key={item.id} type="button" role="tab" id={`tab-${item.id}`} aria-selected={isActive} aria-controls={`panel-${item.id}`} onClick={() => setActive(item.id)} className={`px-5 py-3 rounded-full text-sm font-medium whitespace-nowrap border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ${isActive ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-surface hover:border-accent hover:text-accent'}`} data-id="element-43" data-mp="18qfzm3ed2fbf">
                {item.name}
              </button>;
        })}
        </div>

        <motion.div key={current.id} id={`panel-${current.id}`} role="tabpanel" aria-labelledby={`tab-${current.id}`} initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start" data-id="element-44" data-mp="ba52syhwg8sh">
          <div className="lg:col-span-7" data-id="element-45" data-mp="u40sfvsmmldw">
            <Gallery photos={current.photos} resetKey={current.id} badge={current.badge} data-id="element-46" data-mp="1815w5p4vb85z" />
          </div>

          <div className="lg:col-span-5 lg:pt-4" data-id="element-47" data-mp="130jaue38eqw8">
            <h3 className="text-2xl md:text-3xl font-serif text-primary mb-3" data-id="element-48" data-mp="2xhrnlo10f0ns">
              {current.name}
            </h3>
            <p className="flex items-center gap-2 text-sm font-medium text-accent mb-5" data-id="element-49" data-mp="32gcprsztnuev">
              <Users className="w-4 h-4 shrink-0" data-id="element-50" data-mp="379xcupzvn3za" />
              {current.capacity}
            </p>
            <p className="text-gray-600 leading-relaxed mb-8" data-id="element-51" data-mp="3b8xwgaq6ianc">
              {current.description}
            </p>

            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8" data-id="element-52" data-mp="2elt4fwpx8csw">
              {current.features.map(feature => {
              const Icon = feature.icon;
              return <li key={feature.label} className="flex items-center gap-2 text-sm text-gray-700" data-id="element-53" data-mp="2njbuxgmc3jc6">
                    <Icon className="w-4 h-4 text-accent shrink-0" data-id="element-54" data-mp="2v7cyzvf3ski" />
                    <span data-id="element-55" data-mp="7timtalpq32q">{feature.label}</span>
                  </li>;
            })}
            </ul>

            <a href="#contacto" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-accent text-white font-medium whitespace-nowrap hover:bg-accent-light transition-colors" data-id="element-56" data-mp="hxz1i0nghe4y">
              {t('Consultar disponibilidad', 'Check availability')}
              <ArrowRight className="w-4 h-4" data-id="element-57" data-mp="46kj9w1dhgyy" />
            </a>
          </div>
        </motion.div>

        <p className="mt-12 flex items-center gap-2 text-sm text-gray-500" data-id="element-58" data-mp="3vcvdp9bvnjhb">
          <Users className="w-4 h-4 text-accent shrink-0" data-id="element-59" data-mp="3qd8y4t8ktr5c" />
          {t('Contanos las fechas, la cantidad de huéspedes y te asignamos la unidad que mejor se adapte.', 'Tell us your dates and number of guests and we will assign the unit that best fits.')}
        </p>
      </div>
    </section>;
}